import { motion } from "framer-motion";
import { ShieldCheck, Camera, FileText, BadgeCheck } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { renderLinkedText } from "@/lib/content";

const BADGE_ICONS = [ShieldCheck, Camera, FileText, BadgeCheck];

export const Trust = () => {
  const { content } = useContent();
  const t = content.trust;
  return (
  <section
    className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-40"
    data-testid="trust-section"
  >
    <div className="grid items-start gap-16 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="mb-6 text-[11px] font-bold uppercase tracking-[0.3em] text-[#7A2B21]">
            {t.kicker}
          </p>
          <h2 className="font-display text-4xl font-medium leading-[1.1] tracking-tight text-[#1C1917] sm:text-5xl">
            {t.title_line1}{" "}
            <span className="italic text-[#7A2B21]">{t.title_line2}</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-[#57534E]">
            {t.intro}
          </p>

          <motion.div
            initial={{ opacity: 0, y: 30, rotate: -2 }}
            whileInView={{ opacity: 1, y: 0, rotate: -1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 rounded-3xl bg-white p-8 shadow-[0_30px_60px_rgba(28,25,23,0.12)]"
            data-testid="sample-visit-report"
          >
            <div className="mb-6 flex items-center justify-between border-b border-dashed border-[#1C1917]/15 pb-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#7A2B21]">
                  Visit Report · Sample
                </p>
                <p className="mt-1 font-display text-lg italic text-[#1C1917]">
                  {t.report.location}
                </p>
              </div>
              <Camera size={20} className="text-[#C5A059]" />
            </div>
            <dl className="space-y-4">
              {t.report.rows.map((r) => (
                <div key={r.label} className="flex justify-between gap-4">
                  <dt className="text-xs font-semibold uppercase tracking-wider text-[#57534E]">
                    {r.label}
                  </dt>
                  <dd className="text-right text-xs text-[#1C1917]">{r.value}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-6 flex gap-2">
              {[0, 1, 2].map((n) => (
                <div
                  key={n}
                  className="h-14 flex-1 rounded-xl bg-[#2C4C3B]/10"
                  aria-hidden="true"
                />
              ))}
            </div>
            <p className="mt-3 text-center text-[10px] uppercase tracking-[0.2em] text-[#57534E]">
              {t.report.photos_note}
            </p>
          </motion.div>
        </motion.div>
      </div>

      <div className="lg:col-span-7">
        <div className="grid gap-4 sm:grid-cols-2">
          {t.badges.map((text, i) => {
            const Icon = BADGE_ICONS[i] || ShieldCheck;
            return (
            <motion.div
              key={text}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              className="flex items-center gap-4 rounded-2xl border border-[#1C1917]/10 bg-white/60 px-6 py-5"
              data-testid={`trust-badge-${i}`}
            >
              <Icon size={20} className="shrink-0 text-[#7A2B21]" />
              <span className="text-sm font-semibold text-[#1C1917]">{text}</span>
            </motion.div>
            );
          })}
        </div>

        <div className="mt-12">
          <p className="mb-6 text-[11px] font-bold uppercase tracking-[0.3em] text-[#7A2B21]">
            {t.commitments_title}
          </p>
          <div className="space-y-6">
            {t.commitments.map((v, i) => (
            <motion.blockquote
              key={v.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="border-l-2 border-[#C5A059] pl-8"
              data-testid={`trust-voice-${i}`}
            >
              <p className="font-display text-xl italic leading-relaxed text-[#1C1917] md:text-2xl">
                {renderLinkedText(`"${v.quote}"`)}
              </p>
              <footer className="mt-3 text-xs font-bold uppercase tracking-[0.2em] text-[#7A2B21]">
                {v.name}
              </footer>
            </motion.blockquote>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};
